import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { Toast } from 'components';
import { TOAST_TYPES_ENUM } from 'enums';
import { queryClient } from 'App';
import { mutateWithOffline } from 'utils/mutateWithOffline';
import axiosInstance from 'axiosConfig';
import { upsertOfflineMeasurementInfo } from 'hooks/useOfflineMeasurementInfo';
import { resolveOfflineUuid } from 'hooks/useOfflineUuidMap';
import {
  getOfflineMeasurementByUuid,
  updateOfflineMeasurement,
} from 'hooks/useOfflineMeasurements';

type MeasurementInfoData = {
  measurementUuid: string;
  roomName: string;
  holeSizeWidth: string;
  holeSizeHeight: string;
  joinerySizeWidth: string;
  joinerySizeHeight: string;
  windowComment?: string;
  windowBottomAddition: 'clinarite' | 'systemicExpansion' | 'warmWindowSill' | 'undersillStrip' | 'other';
  windowBottomAdditionSize: string;
  windowSideAddition: 'widening' | 'miniConnector' | '90connector' | 'pipeConnector' | '45connector' | 'other';
  windowSideAdditionSize: string;
  typeOfSlidingWindow: 'hst' | 'smartSlide' | 'psk';
  comment?: string;
  windowAdditionUuids: any[];

  // pola, które formularz wysyła (i backend uwzględnia)
  warningComment?: string;
  windowBottomAdditionComment?: string;
  windowSideAdditionComment?: string;

  // obecnie formularz przekazuje też drawing/file; backend przyjmuje, a my w offline ignorujemy upload
  drawing?: any;
  file?: any[];
  commentsWindowAddition?: string;
};

const usePutMeasurementInfo = (
  options?: UseMutationOptions<any, Error, MeasurementInfoData>
) => {
  return useMutation({
    ...options,
    mutationKey: ['editMeasurementInfo'],
    mutationFn: async (data: MeasurementInfoData) => {
      // ONLINE: musimy dostać realną odpowiedź z backendu (measurementInfo.uuid),
      // a mutateWithOffline potrafi wrzucić do kolejki nawet przy navigator.onLine===true
      // (np. gdy payload zawiera File/Blob w polu drawing).
      if (navigator.onLine) {
        const response = await axiosInstance.request<any>({
          method: 'PUT',
          url: '/measurement/info',
          data,
        });
        return response.data;
      }

      // W OFFLINE nie wysyłamy binarki w tym endpointcie (upload idzie przez /files).
      // W ONLINE chcemy prawdziwą odpowiedź z backendu, bo jest tam realny measurementInfo.uuid
      // (parentUuid do /files).
      const bodyForOffline = {
        ...data,
        drawing: undefined,
      } as MeasurementInfoData;

      return mutateWithOffline<any>('PUT', `/measurement/info`, bodyForOffline, {
        entityType: 'measurement',
        dedupeKey: `measurement:measurement-info:${resolveOfflineUuid(data.measurementUuid)}`,
        forceOffline: true,
        simulateResponse: ({ tempId }) => {
          const now = new Date().toISOString();
          const effectiveUuid = resolveOfflineUuid(data.measurementUuid);

          upsertOfflineMeasurementInfo({
            measurementUuid: effectiveUuid,
            roomName: data.roomName,
            holeSizeWidth: data.holeSizeWidth,
            holeSizeHeight: data.holeSizeHeight,
            joinerySizeWidth: data.joinerySizeWidth,
            joinerySizeHeight: data.joinerySizeHeight,
            windowComment: data.windowComment ?? null,
            windowBottomAddition: data.windowBottomAddition,
            windowBottomAdditionSize: data.windowBottomAdditionSize,
            windowBottomAdditionComment: (data as any).windowBottomAdditionComment ?? null,
            windowSideAddition: data.windowSideAddition,
            windowSideAdditionSize: data.windowSideAdditionSize,
            windowSideAdditionComment: (data as any).windowSideAdditionComment ?? null,
            typeOfSlidingWindow: (data as any).typeOfSlidingWindow ?? null,
            comment: (data as any).comment ?? null,
            warningComment: (data as any).warningComment ?? null,
            windowAdditionUuids: (data as any).windowAdditionUuids || [],
            file: (data as any).file || [],
            updatedAt: now,
          });

          // Ostatni krok: oznaczamy pomiar jako zakończony, żeby lista/refresh
          // zachowywały się podobnie do backendu.
          const listItem = getOfflineMeasurementByUuid(effectiveUuid);
          if (listItem?.orderUuid) {
            updateOfflineMeasurement(listItem.orderUuid, effectiveUuid, {
              roomName: data.roomName,
              isFinished: 1,
              lastFinishedPage:
                typeof listItem.pageCount === 'number' && listItem.pageCount > 0
                  ? listItem.pageCount
                  : listItem.lastFinishedPage,
              updatedAt: now,
            });
          }

          return {
            status: 'success',
            message: 'Measurement updated (offline)',
            measurement: {
              uuid: effectiveUuid,
              measurementInfo: {
                uuid: tempId,
                roomName: data.roomName,
                holeSizeWidth: data.holeSizeWidth,
                holeSizeHeight: data.holeSizeHeight,
                joinerySizeWidth: data.joinerySizeWidth,
                joinerySizeHeight: data.joinerySizeHeight,
                windowComment: data.windowComment ?? null,
                windowBottomAddition: data.windowBottomAddition,
                windowBottomAdditionSize: data.windowBottomAdditionSize,
                windowBottomAdditionComment:
                  (data as any).windowBottomAdditionComment ?? null,
                windowSideAddition: data.windowSideAddition,
                windowSideAdditionSize: data.windowSideAdditionSize,
                windowSideAdditionComment:
                  (data as any).windowSideAdditionComment ?? null,
                typeOfSlidingWindow: (data as any).typeOfSlidingWindow ?? null,
                comment: (data as any).comment ?? null,
                warningComment: (data as any).warningComment ?? null,
                windowAdditionUuids: (data as any).windowAdditionUuids || [],
                file: (data as any).file || [],
                createdAt: now,
                updatedAt: now,
                id: null,
                measurementId: null,
                // online zwraca windowAddition jako obiekty; w offline nie mamy słownika
                windowAddition: [],
              },
            },
          };
        },
      });
    },
    onMutate: (variables) => {
      Toast({
        message: 'Przetwarzanie informacji o pomiarze...',
        type: TOAST_TYPES_ENUM.LOADING,
        id: 'edit-measurement-info',
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      Toast({
        message: 'Informacje o pomiarze zaktualizowane pomyślnie.',
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: 'edit-measurement-info',
      });
      queryClient.invalidateQueries({ queryKey: ['measurementInfo'] });
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
      const listItem = getOfflineMeasurementByUuid(
        resolveOfflineUuid(variables.measurementUuid)
      );
      if (listItem?.orderUuid) {
        queryClient.invalidateQueries({
          queryKey: ['measurements', listItem.orderUuid],
        });
      }
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      Toast({
        message: 'Błąd podczas aktualizacji informacji o pomiarze.',
        type: TOAST_TYPES_ENUM.ERROR,
        id: 'edit-measurement-info',
      });
      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default usePutMeasurementInfo;
